import { Component, Input } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';

import { ApiShivaService } from '@core/apis/api-shiva.service';
import { EmailModalComponent } from '@core/components/email-modal/email-modal.component';
import { EMAIL_TEMPLATES_OPTIONS } from '@core/constants';
import { IContact, IOperatorLine, ITemplateEmail, IWorkOrderItems } from '@core/interfaces';
import { PromisesService } from '@core/services/promises.service';
import { SignalsService } from '@core/services/signals.service';
import { WcmUsersService } from '@core/services/wcm-users.service';
import {EmailService} from '@core/services/email.services';

interface IContext {
  address_from: string;
  address_to: string[];
  message_html: string;
  message_attachment_ids: string[];
  cc_addresses_list: string[];
  subject: string;
}

interface IEmailModal {
  attachments: Array<number>;
  cc: Array<string>;
  content: string;
  emailTo: string;
  senderAddress: string;
  subject: string;
}

interface IUtils {
  city: string;
  notif_reminder_date: string;
  provider: string;
  code_woi: string;
  site_customer_ref: string;
  site_name: string;
  technology: string;
  contact?: IContact;
}

@Component({
  selector: 'app-reminder-email-button',
  templateUrl: './reminder-email-button.component.html',
})
export class ReminderEmailButtonComponent {

  @Input() public label?: string;
  @Input() public type: keyof typeof EMAIL_TEMPLATES_OPTIONS;
  @Input() public woi: IWorkOrderItems;
  @Input() public operatorLine: IOperatorLine;
  @Input() public disabled: boolean;

  constructor(
    private emailService: EmailService,
    private readonly promisesService: PromisesService,
    private readonly toastr: ToastrService,
    private readonly ngbModal: NgbModal,
    private readonly signalsService: SignalsService,
    private readonly apiShiva: ApiShivaService,
    private readonly wcmUsersService: WcmUsersService,
  ) {
  }

  public sendReminder(): void {
    const emailTo: string | undefined = this.woi?.contact?.email;
    if (!emailTo) {
      this.toastr.error('Impossible de récupérer le mail du contact. Vérifiez le contact de la tâche avant de continuer.');

    } else {
      this.emailService.getShippingMailData(this.woi)
        .then(() => this.emailService.getTemplateEmail(this.type))
        .then((templateEmail: ITemplateEmail) => this._openEmailModal(templateEmail, emailTo))
        .then((context: IContext) => this.apiShiva.work_order_items.send_mail(this.woi.id, context)
          .then(() => this.toastr.success('Le mail a correctement été envoyé.'))
          .catch((err: unknown) => Promise.reject(err))
        )
        .catch(() => {
          // The email modal was dismissed, do nothing
        });
    }
  }

  private _openEmailModal(templateEmail: ITemplateEmail, emailTo: string): Promise<IContext> {
    const deferred = this.promisesService.defer();
    const utilsFillingVar = {
      city: this.woi?.work_order?.entity?.location?.city || '[[Ville entité]]',
      notif_reminder_date: this.emailService.generateRDVDate(this.woi),
      provider: this.operatorLine?.offer?.provider?.name || '[[fournisseur_du_lien]]',
      code_woi: this.woi?.code || '[[code_woi]]',
      site_customer_ref: this.woi?.work_order?.entity?.customer_ref || '[[Ref client entité]]',
      site_name: this.woi?.work_order?.entity?.name || '[[nom_du_site]]',
      technology: this.operatorLine?.offer?.technology?.name || '[[techno_du_lien]]',
      contact: this.woi.contact,
    };

    const emailSubject: string = this._generateEmailSubject(utilsFillingVar, templateEmail.subject);

    const emailsCcAddress: string[] = templateEmail['cc_address']?.split(',').map(value => value.trim()) || ['delivery@waycom.net'];
    // if an assignee (not the one on our woi but on the order) is detected we add him to the cc list
    if (this?.woi?.work_order?.order?.assignee) {
      const assignee = this.wcmUsersService.get(this.woi.work_order.order.assignee);
      emailsCcAddress.push(assignee.email);
    }

    const modal: NgbModalRef = this.ngbModal.open(EmailModalComponent, {
      backdrop: 'static',
      size: 'lg'
    });
    modal.componentInstance.attachmentsApi = this.apiShiva.attachments_ng;
    modal.componentInstance.attachmentModel = 'work-order-items';
    modal.componentInstance.attachmentModelPk = this.woi.id;
    modal.componentInstance.defaultEmailSubject = emailSubject;
    modal.componentInstance.templateEmailCcAddress = emailsCcAddress;
    modal.componentInstance.emailTo = emailTo;
    modal.componentInstance.fillFunction = (template: string): string => this.emailService.getEmailVariables(template,this.type, this.woi, this.operatorLine);
    modal.componentInstance.modalSendButton = `Envoyer la notification`;
    modal.componentInstance.modalTitle = `Mail pour notifier le client`;
    modal.componentInstance.templateEmailId = templateEmail.id;
    modal.componentInstance.templateEmailLastAttachmentId = templateEmail.last_attachment_id;
    modal.componentInstance.templateEmailSenderAddress = templateEmail.sender_address;
    modal.componentInstance.type = 'delivery';
    modal.componentInstance.typeEmail = this.woi.product.code;

    modal.result
      .then(
        (res: IEmailModal) => {
          const context: IContext = {
            address_from: res.senderAddress,
            address_to: [res.emailTo],
            message_html: res.content,
            message_attachment_ids: res.attachments.map((attachmentId: number): string => `${attachmentId}`),
            cc_addresses_list: res.cc,
            subject: res.subject
          };
          this.signalsService.broadcast('attachments-list-refresh');
          // Resolving the transition with email content in context

          deferred.resolve(context);
        },
        () => {
        })
      .catch(() => deferred.reject());
    return deferred.promise;
  }

  private _generateEmailSubject(utils: IUtils, subject: string): string {
    const defaultEmailSubject: string = subject ? subject : `[IMPORTANT] ADISTA – RDV D’INTERVENTION LIVRAISON LIAISON INTERNET ${utils.technology} ${utils.provider} – Réf : ${utils.code_woi}  ${utils.site_name} - ${utils.city} - ${utils.site_customer_ref}`;

    const finalSubject: string = this.emailService.getEmailVariables(defaultEmailSubject, this.type, this.woi, this.operatorLine);
    if (finalSubject.includes('[[')) {
      this.toastr.warning(`Attention avant d'envoyer votre mail car votre OBJET possède encore des variables à modifier`);
    }
    return finalSubject;
  }
}
